<script>
import { Annotation } from '@/api';
import GeoJSON from 'ol/format/GeoJSON';
import WKT from 'ol/format/WKT';
import Point from 'ol/geom/Point';
import LayerGroup from 'ol/layer/Group';
import VectorLayer from 'ol/layer/Vector';
import { unByKey } from 'ol/Observable';
import VectorSource from 'ol/source/Vector';
import { Fill, Stroke, Style, Text } from 'ol/style';
import layer from 'vuelayers/lib/mixin/layer';
import { queryMalariaOverlay } from './ai-models/malaria-overlay-query';
import createMalariaOverlayWorker from './workers/create-malaria-overlay-worker';

const PARASITE_COLOR = '#23d160';
const WBC_COLOR = '#3e8ed0';
const PARASITE_FILL = 'rgba(35, 209, 96, 0.08)';
const WBC_FILL = 'rgba(62, 142, 208, 0.08)';
const DEFAULT_OTHER_TERM_COLOR = '#9575CD';
const USER_ANNOTATION_CLASS = 'be.cytomine.ontology.UserAnnotation';
const FEATURE_BUILD_BATCH_SIZE = 500;
const FEATURE_BUILD_TIME_BUDGET_MS = 8;

function colorWithOpacity(color, opacity) {
  const match = /^#([0-9a-f]{6})$/i.exec(color || '');
  if (!match) {
    return color;
  }
  const value = match[1];
  return `rgba(${parseInt(value.slice(0, 2), 16)}, ${parseInt(value.slice(2, 4), 16)}, ${parseInt(value.slice(4, 6), 16)}, ${opacity})`;
}

export default {
  name: 'malaria-detection-overlay-layer',
  mixins: [layer],
  props: {
    annotations: {
      type: Array,
      default: () => []
    },
    thresholds: {
      type: Object,
      default: () => ({ byType: { parasite: 0.65, wbc: 0.65 } })
    },
    byTypeVisible: {
      type: Object,
      default: () => ({ parasite: true, wbc: true })
    },
    showConfidenceLabelsByType: {
      type: Object,
      default: () => ({ parasite: false, wbc: false })
    }
  },
  data() {
    return {
      format: new WKT(),
      geojson: new GeoJSON(),
      overlayLayer: null,
      partitionSources: {},
      partitionLayers: {},
      partitionSettingSnapshots: {
        thresholds: {},
        visibility: {},
        confidence: {}
      },
      styleCache: new Map(),
      featureBuildFrame: null,
      featureBuildUsesAnimationFrame: false,
      featureBuildToken: 0,
      isBuildingFeatures: false,
      featureBuildTracksLoading: false,
      activeFeatureBuildScope: null,
      pendingFeatureBuilds: {},
      featureBuildProgress: {
        completed: 0,
        total: 0
      },
      viewportWorker: null,
      viewportRecords: null,
      viewportRequestId: 0,
      latestViewportRequestIds: {},
      mapMoveEndKey: null,
      initialViewportBuilt: false
    };
  },
  methods: {
    createLayer() {
      const records = this.buildViewportRecords();
      this.syncPartitions(records.types);
      this.overlayLayer = new LayerGroup({
        id: this.id,
        layers: Object.values(this.partitionLayers),
        visible: this.visible,
        opacity: this.opacity,
        extent: this.extent,
        zIndex: this.zIndex
      });
      this.capturePartitionSettingSnapshots();
      this.startViewportWorker(records);
      return this.overlayLayer;
    },
    overlayItems() {
      return this.annotations;
    },
    partitionKey(value) {
      const rawType = String(value?.type ?? value ?? '').toLowerCase();
      return ['wbc', 'white_blood_cell', 'white blood cell'].includes(rawType)
        ? 'wbc'
        : rawType || 'other';
    },
    ensurePartition(partitionKey) {
      const key = this.partitionKey(partitionKey);
      if (this.partitionSources[key]) {
        return;
      }
      const source = new VectorSource({ features: [] });
      const partitionLayer = new VectorLayer({
        id: `${this.id}-${key}`,
        source,
        declutter: true,
        style: this.styleFeature
      });
      this.partitionSources[key] = source;
      this.partitionLayers[key] = partitionLayer;
      if (this.overlayLayer?.getLayers) {
        this.overlayLayer.getLayers().push(partitionLayer);
      }
    },
    syncPartitions(types = []) {
      Array.from(new Set(types.map(type => this.partitionKey(type))))
        .forEach(key => this.ensurePartition(key));
    },
    capturePartitionSettingSnapshots() {
      this.partitionSettingSnapshots = {
        thresholds: { ...(this.thresholds?.byType || {}) },
        visibility: { ...(this.byTypeVisible || {}) },
        confidence: { ...(this.showConfidenceLabelsByType || {}) }
      };
    },
    changedPartitionSettingKeys(setting, values = {}) {
      const previous = this.partitionSettingSnapshots[setting] || {};
      const keys = new Set([...Object.keys(previous), ...Object.keys(values)]);
      const changed = Array.from(keys)
        .map(key => this.partitionKey(key))
        .filter((key, index, all) => (
          all.indexOf(key) === index &&
          previous[key] !== values[key]
        ));
      this.partitionSettingSnapshots = {
        ...this.partitionSettingSnapshots,
        [setting]: { ...values }
      };
      return changed;
    },
    decodedLocation(item) {
      if (!item.location) {
        return null;
      }
      if (!item.geometryCompression) {
        return item.location;
      }
      try {
        return decodeURIComponent(atob(item.location));
      } catch (error) {
        return item.location;
      }
    },
    featureForItem(item) {
      const location = this.decodedLocation(item);
      if (!location && !item.preparedGeometry) {
        return null;
      }
      let feature;
      try {
        feature = item.preparedGeometry
          ? this.geojson.readFeature({
            type: 'Feature',
            geometry: item.preparedGeometry
          })
          : this.format.readFeature(location);
      } catch (error) {
        return null;
      }
      const annot = this.annotationForItem(item, location, feature);
      feature.setId(annot.id);
      feature.set('annot', annot);
      feature.set('id', annot.id);
      feature.set('type', item.type);
      feature.set('isAiVisualizationOverlay', true);
      feature.set('termColor', item.termColor);
      feature.set('confidence', Number(item.confidence) || 0);
      feature.set('showConfidenceLabel', item.showConfidenceLabel !== false);
      feature.set('overlaySourceItem', item.overlaySourceItem || item);
      return feature;
    },
    annotationForItem(item, location, feature) {
      const extent = feature.getGeometry().getExtent();
      const centroid = item.centroid || {
        x: (extent[0] + extent[2]) / 2,
        y: (extent[1] + extent[3]) / 2
      };
      const timestamp = Number(item.created) || Number(item.updated) || Date.now();
      const annot = new Annotation({
        id: item.id,
        class: item.class || USER_ANNOTATION_CLASS,
        created: timestamp,
        updated: Number(item.updated) || timestamp,
        project: item.project,
        image: item.image,
        slice: item.slice,
        user: item.user,
        location,
        geometryCompression: false,
        centroid,
        area: item.area,
        areaUnit: item.areaUnit,
        perimeter: item.perimeter,
        perimeterUnit: item.perimeterUnit,
        term: item.term || []
      });
      annot.confidence = item.confidence;
      annot.aiVisualizationType = item.aiVisualizationType;
      annot.malariaDetectionType = item.type;
      annot.termColor = item.termColor;
      return annot;
    },
    createFeatures(items) {
      return (items || [])
        .filter(item => item.visible !== false)
        .map(item => this.featureForItem(item))
        .filter(Boolean);
    },
    buildViewportRecords() {
      const items = this.overlayItems();
      const coordinates = new Float64Array(items.length * 2);
      const confidences = new Float32Array(items.length);
      const geometryCompression = new Uint8Array(items.length);
      const types = new Array(items.length);
      const locations = new Array(items.length);

      items.forEach((item, index) => {
        const centroid = item.centroid || {};
        coordinates[index * 2] = Number(centroid.x ?? item.x) || 0;
        coordinates[index * 2 + 1] = Number(centroid.y ?? item.y) || 0;
        confidences[index] = Number(item.confidence) || 0;
        geometryCompression[index] = item.geometryCompression ? 1 : 0;
        types[index] = this.partitionKey(item);
        locations[index] = item.location || '';
      });

      return {
        coordinates,
        confidences,
        geometryCompression,
        types,
        locations
      };
    },
    startViewportWorker(preparedRecords = null) {
      this.stopViewportWorker();
      this.initialViewportBuilt = false;
      const records = preparedRecords || this.buildViewportRecords();
      this.syncPartitions(records.types);
      const total = records.types.length;
      this.$emit('build-start', { completed: 0, total });
      this.viewportWorker = createMalariaOverlayWorker();

      if (!this.viewportWorker) {
        this.viewportRecords = records;
        this.attachMapViewportListener();
        this.requestViewportQuery();
        return;
      }

      this.viewportWorker.onmessage = event => this.handleViewportWorkerMessage(event.data || {});
      this.viewportWorker.onerror = () => {
        this.viewportWorker?.terminate();
        this.viewportWorker = null;
        this.viewportRecords = this.buildViewportRecords();
        this.attachMapViewportListener();
        this.requestViewportQuery();
      };
      this.viewportWorker.postMessage({
        type: 'initialize',
        ...records
      }, [
        records.coordinates.buffer,
        records.confidences.buffer,
        records.geometryCompression.buffer
      ]);
    },
    stopViewportWorker() {
      if (this.mapMoveEndKey) {
        unByKey(this.mapMoveEndKey);
        this.mapMoveEndKey = null;
      }
      if (this.viewportWorker) {
        this.viewportWorker.terminate();
        this.viewportWorker = null;
      }
      this.viewportRecords = null;
    },
    attachMapViewportListener() {
      if (!this.$map || this.mapMoveEndKey) {
        return;
      }
      this.mapMoveEndKey = this.$map.on('moveend', () => this.requestViewportQuery());
    },
    viewportQueryOptions() {
      if (!this.$map) {
        return null;
      }
      const view = this.$map.getView();
      const size = this.$map.getSize();
      if (!view || !size) {
        return null;
      }
      return {
        extent: view.calculateExtent(size),
        resolution: view.getResolution(),
        thresholds: {
          ...(this.thresholds?.byType || {})
        },
        byTypeVisible: {
          ...(this.byTypeVisible || {})
        }
      };
    },
    requestViewportQuery(partitionKey = null) {
      const options = this.viewportQueryOptions();
      if (!options) {
        return;
      }
      const normalizedPartitionKey = partitionKey
        ? this.partitionKey(partitionKey)
        : null;
      const targetPartitionKeys = normalizedPartitionKey
        ? [normalizedPartitionKey]
        : Object.keys(this.partitionSources);
      const requestId = ++this.viewportRequestId;
      targetPartitionKeys.forEach(key => {
        this.latestViewportRequestIds[key] = requestId;
      });
      const message = {
        type: 'query',
        requestId,
        partitionKey: normalizedPartitionKey,
        ...(normalizedPartitionKey ? { types: [normalizedPartitionKey] } : {}),
        ...options
      };

      if (this.viewportWorker) {
        this.viewportWorker.postMessage(message);
        return;
      }
      if (!this.viewportRecords) {
        this.viewportRecords = this.buildViewportRecords();
      }
      const result = queryMalariaOverlay(this.viewportRecords, message);
      this.applyViewportQueryResult({
        type: 'query-result',
        requestId,
        partitionKey: normalizedPartitionKey,
        features: result.indices.map(index => ({ index, geometry: null }))
      });
    },
    handleViewportWorkerMessage(message) {
      if (message.type === 'ready') {
        this.attachMapViewportListener();
        this.requestViewportQuery();
        return;
      }
      if (message.type === 'query-result') {
        this.applyViewportQueryResult(message);
      }
    },
    applyViewportQueryResult(result) {
      const annotations = this.overlayItems();
      const targetPartitionKeys = result.partitionKey
        ? [this.partitionKey(result.partitionKey)]
        : Object.keys(this.partitionSources);
      const currentPartitionKeys = targetPartitionKeys.filter(key => (
        this.latestViewportRequestIds[key] === result.requestId
      ));
      if (!currentPartitionKeys.length) {
        return;
      }
      const currentPartitionSet = new Set(currentPartitionKeys);
      const items = (result.features || [])
        .map(feature => {
          const sourceItem = annotations[feature.index];
          return {
            ...sourceItem,
            preparedGeometry: feature.geometry,
            overlaySourceItem: sourceItem
          };
        })
        .filter(item => currentPartitionSet.has(this.partitionKey(item)));
      const trackLoading = !this.initialViewportBuilt;
      this.startFeatureBuild(items, {
        partitionKeys: currentPartitionKeys,
        trackLoading
      });
      this.initialViewportBuilt = true;
    },
    scheduleFeatureBuild(callback) {
      if (typeof requestAnimationFrame === 'function') {
        this.featureBuildUsesAnimationFrame = true;
        this.featureBuildFrame = requestAnimationFrame(callback);
        return;
      }
      this.featureBuildUsesAnimationFrame = false;
      this.featureBuildFrame = setTimeout(callback, 0);
    },
    cancelFeatureBuild({ clearPending = true } = {}) {
      this.featureBuildToken += 1;
      if (this.featureBuildFrame !== null) {
        if (this.featureBuildUsesAnimationFrame && typeof cancelAnimationFrame === 'function') {
          cancelAnimationFrame(this.featureBuildFrame);
        } else {
          clearTimeout(this.featureBuildFrame);
        }
      }
      this.featureBuildFrame = null;
      this.isBuildingFeatures = false;
      this.featureBuildTracksLoading = false;
      this.activeFeatureBuildScope = null;
      if (clearPending) {
        this.pendingFeatureBuilds = {};
      }
    },
    startNextPendingFeatureBuild() {
      const [scope] = Object.keys(this.pendingFeatureBuilds);
      if (!scope) {
        return;
      }
      const pendingBuild = this.pendingFeatureBuilds[scope];
      delete this.pendingFeatureBuilds[scope];
      this.startFeatureBuild(pendingBuild.items, pendingBuild.options);
    },
    startFeatureBuild(items, { partitionKeys = null, trackLoading = true } = {}) {
      const visibleItems = (items || []).filter(item => item.visible !== false);
      const targetPartitionKeys = partitionKeys?.length
        ? partitionKeys.map(key => this.partitionKey(key))
        : Array.from(new Set(visibleItems.map(item => this.partitionKey(item))));
      const buildScope = [...targetPartitionKeys].sort().join('|') || 'empty';
      if (this.isBuildingFeatures && this.activeFeatureBuildScope !== buildScope) {
        this.pendingFeatureBuilds[buildScope] = {
          items,
          options: {
            partitionKeys: targetPartitionKeys,
            trackLoading
          }
        };
        return;
      }
      const shouldTrackLoading = trackLoading || (
        this.isBuildingFeatures && this.featureBuildTracksLoading
      );
      this.cancelFeatureBuild({ clearPending: false });
      const buildToken = this.featureBuildToken;
      const partitionSources = this.partitionSources || {};
      const sourceForPartition = key => partitionSources[key] || this.overlaySource;
      const itemsByPartition = {};
      targetPartitionKeys.forEach(key => {
        itemsByPartition[key] = new Map();
      });
      visibleItems.forEach(item => {
        const key = this.partitionKey(item);
        if (
          itemsByPartition[key] &&
          item.id !== undefined &&
          item.id !== null
        ) {
          itemsByPartition[key].set(String(item.id), item);
        }
      });
      const operations = [];
      targetPartitionKeys.forEach(key => {
        const source = sourceForPartition(key);
        const nextItems = itemsByPartition[key] || new Map();
        if (!source?.getFeatures || !source?.removeFeature) {
          source?.clear?.();
          nextItems.forEach(item => operations.push({ type: 'add', key, item }));
          return;
        }
        source.getFeatures().forEach(feature => {
          const id = String(feature.getId());
          if (nextItems.has(id)) {
            const nextItem = nextItems.get(id);
            nextItems.delete(id);
            if (
              typeof feature.get === 'function' &&
              !this.updateFeatureFromItem(feature, nextItem)
            ) {
              operations.push({ type: 'remove', source, feature });
              operations.push({ type: 'add', key, item: nextItem });
            }
          } else {
            operations.push({ type: 'remove', source, feature });
          }
        });
        nextItems.forEach(item => operations.push({ type: 'add', key, item }));
      });
      const total = operations.length;
      let completed = 0;
      this.isBuildingFeatures = true;
      this.featureBuildTracksLoading = shouldTrackLoading;
      this.activeFeatureBuildScope = buildScope;
      this.featureBuildProgress = { completed, total };
      if (shouldTrackLoading) {
        this.$emit('build-start', this.featureBuildProgress);
      }

      const buildNextBatch = () => {
        if (buildToken !== this.featureBuildToken) {
          return;
        }
        this.featureBuildFrame = null;
        const startedAt = typeof performance !== 'undefined' ? performance.now() : Date.now();
        const featuresByPartition = {};
        let processed = 0;
        while (completed < total && processed < FEATURE_BUILD_BATCH_SIZE) {
          const operation = operations[completed];
          if (operation.type === 'remove') {
            operation.source.removeFeature(operation.feature);
          } else {
            const feature = this.featureForItem(operation.item);
            if (feature) {
              (featuresByPartition[operation.key] ||= []).push(feature);
            }
          }
          completed += 1;
          processed += 1;
          const currentTime = typeof performance !== 'undefined' ? performance.now() : Date.now();
          if (currentTime - startedAt >= FEATURE_BUILD_TIME_BUDGET_MS) {
            break;
          }
        }
        Object.entries(featuresByPartition).forEach(([partitionKey, features]) => {
          sourceForPartition(partitionKey)?.addFeatures?.(features);
        });
        this.featureBuildProgress = { completed, total };
        if (shouldTrackLoading) {
          this.$emit('build-progress', this.featureBuildProgress);
        }

        if (completed < total) {
          this.scheduleFeatureBuild(buildNextBatch);
          return;
        }
        this.isBuildingFeatures = false;
        this.featureBuildTracksLoading = false;
        this.activeFeatureBuildScope = null;
        if (shouldTrackLoading) {
          this.$emit('build-complete', this.featureBuildProgress);
        }
        this.startNextPendingFeatureBuild();
      };

      this.scheduleFeatureBuild(buildNextBatch);
    },
    updateFeatureFromItem(feature, item) {
      const sourceItem = item.overlaySourceItem || item;
      if (feature.get('overlaySourceItem') === sourceItem) {
        return true;
      }
      const location = this.decodedLocation(item);
      if (!location) {
        return false;
      }

      const annot = feature.get('annot');
      if (!annot || annot.location !== location) {
        const nextFeature = this.featureForItem(item);
        if (!nextFeature) {
          return false;
        }
        feature.setGeometry(nextFeature.getGeometry());
        ['annot', 'id', 'type', 'isAiVisualizationOverlay', 'termColor', 'confidence', 'showConfidenceLabel'].forEach(property => {
          feature.set(property, nextFeature.get(property));
        });
        feature.set('overlaySourceItem', sourceItem);
        feature.changed();
        return true;
      }

      const timestamp = Number(item.created) || Number(item.updated) || annot.created || Date.now();
      Object.assign(annot, {
        class: item.class || USER_ANNOTATION_CLASS,
        created: timestamp,
        updated: Number(item.updated) || timestamp,
        project: item.project,
        image: item.image,
        slice: item.slice,
        user: item.user,
        location,
        geometryCompression: false,
        centroid: item.centroid || annot.centroid,
        area: item.area,
        areaUnit: item.areaUnit,
        perimeter: item.perimeter,
        perimeterUnit: item.perimeterUnit,
        term: item.term || [],
        confidence: item.confidence,
        aiVisualizationType: item.aiVisualizationType,
        malariaDetectionType: item.type,
        termColor: item.termColor
      });
      feature.set('annot', annot);
      feature.set('id', annot.id);
      feature.set('type', item.type);
      feature.set('isAiVisualizationOverlay', true);
      feature.set('termColor', item.termColor);
      feature.set('confidence', Number(item.confidence) || 0);
      feature.set('showConfidenceLabel', item.showConfidenceLabel !== false);
      feature.set('overlaySourceItem', sourceItem);
      feature.changed();
      return true;
    },
    styleFeature(feature) {
      const rawType = String(feature.get('type') || '').toLowerCase();
      const type = ['wbc', 'white_blood_cell', 'white blood cell'].includes(rawType)
        ? 'wbc'
        : rawType;
      const isWbc = ['wbc', 'white_blood_cell', 'white blood cell'].includes(type);
      const isParasite = type === 'parasite';
      const confidence = Number(feature.get('confidence') || 0);
      if (
        (isParasite || isWbc) &&
        (
          this.byTypeVisible?.[type] === false ||
          confidence < Number(this.thresholds?.byType?.[type] ?? 0)
        )
      ) {
        return null;
      }
      const color = isWbc
        ? WBC_COLOR
        : isParasite
          ? PARASITE_COLOR
          : feature.get('termColor') || DEFAULT_OTHER_TERM_COLOR;
      const fill = isWbc
        ? WBC_FILL
        : isParasite
          ? PARASITE_FILL
          : colorWithOpacity(color, 0.08);
      const label = confidence.toFixed(2);
      const showLabel = (
        (isParasite || isWbc) &&
        this.showConfidenceLabelsByType?.[type] === true
      );
      const cacheKey = showLabel
        ? `${type}|${color}|${fill}|label|${label}`
        : `${type}|${color}|${fill}|no-label`;
      if (this.styleCache.has(cacheKey)) {
        return this.styleCache.get(cacheKey);
      }
      const baseStyle = new Style({
        stroke: new Stroke({ color, width: 2 }),
        fill: new Fill({ color: fill })
      });

      if (!showLabel) {
        const styles = [baseStyle];
        this.styleCache.set(cacheKey, styles);
        return styles;
      }

      const styles = [
        baseStyle,
        new Style({
          geometry: this.labelGeometry,
          text: new Text({
            text: label,
            offsetY: -8,
            fill: new Fill({ color }),
            stroke: new Stroke({ color: 'rgba(255,255,255,0.9)', width: 3 }),
            font: '700 12px sans-serif',
            overflow: true,
            textAlign: 'center',
            textBaseline: 'bottom'
          })
        })
      ];
      this.styleCache.set(cacheKey, styles);
      return styles;
    },
    labelGeometry(feature) {
      const extent = feature.getGeometry().getExtent();
      return new Point([
        (extent[0] + extent[2]) / 2,
        extent[3]
      ]);
    },
    refreshFeatures() {
      if (!this.overlaySource) {
        return;
      }

      this.$emit('build-start');
      try {
        const nextItemsById = new Map(
          this.overlayItems()
            .filter(item => item.visible !== false && item.id !== undefined && item.id !== null)
            .map(item => [String(item.id), item])
        );

        this.overlaySource.getFeatures().forEach(feature => {
          const itemId = String(feature.getId());
          const nextItem = nextItemsById.get(itemId);
          if (!nextItem || !this.updateFeatureFromItem(feature, nextItem)) {
            this.overlaySource.removeFeature(feature);
            return;
          }
          nextItemsById.delete(itemId);
        });

        const featuresToAdd = Array.from(nextItemsById.values())
          .map(item => this.featureForItem(item))
          .filter(Boolean);
        if (featuresToAdd.length) {
          this.overlaySource.addFeatures(featuresToAdd);
        }
      } finally {
        this.$emit('build-complete');
      }
    }
  },
  watch: {
    annotations: {
      handler() {
        this.startViewportWorker();
      }
    },
    thresholds: {
      deep: true,
      handler() {
        this.changedPartitionSettingKeys(
          'thresholds',
          this.thresholds?.byType || {}
        ).forEach(key => this.requestViewportQuery(key));
      }
    },
    byTypeVisible: {
      deep: true,
      handler() {
        this.changedPartitionSettingKeys(
          'visibility',
          this.byTypeVisible || {}
        ).forEach(key => this.requestViewportQuery(key));
      }
    },
    showConfidenceLabelsByType: {
      deep: true,
      handler() {
        this.changedPartitionSettingKeys(
          'confidence',
          this.showConfidenceLabelsByType || {}
        ).forEach(key => this.partitionLayers[key]?.changed?.());
      }
    }
  },
  beforeDestroy() {
    const wasBuilding = this.isBuildingFeatures;
    const progress = this.featureBuildProgress;
    this.cancelFeatureBuild();
    this.stopViewportWorker();
    if (wasBuilding) {
      this.$emit('build-complete', {
        ...progress,
        cancelled: true
      });
    }
  }
};
</script>
